import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Collapse, Drawer, Row, Select, Space, Switch, Typography } from "antd";
import {
  CameraOutlined,
  CheckOutlined,
  DeleteOutlined,
  PictureOutlined,
  UndoOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import LengthInput from "./LengthInput";
import {
  REFERENCES,
  TAP_SIGMA,
  isConvex,
  measureLine,
  orderCorners,
  referenceTargets
} from "./photoGeometry";
import { formatLength, formatUncertainty } from "./format";

const MAX_ZOOM = 8;
const LOUPE_SIZE = 128;
const LOUPE_ZOOM = 3;
// Handle and label sizes in screen pixels.
const HANDLE = 9;
const HIT = 22;

const REFERENCE_OPTIONS = [
  { value: "custom", label: "Own rectangle (door, tile, measured wall)" },
  { value: "a4", label: "A4 sheet" },
  { value: "a3", label: "A3 sheet" },
  { value: "card", label: "Bank card" }
];

/**
 * Measures on a photo against a rectangle of known size lying on the same surface. The photo,
 * the reference and the lines stay while the drawer is closed, so width and height of a window
 * can be taken from one photo.
 */
const PhotoMeasure = ({ open, target, onClose }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [src, setSrc] = useState(null);
  const [natural, setNatural] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [scale, setScale] = useState(1); // photo pixels per screen pixel
  const [fit, setFit] = useState(null); // photo width in CSS pixels at zoom 1 (whole photo visible)
  const [refKind, setRefKind] = useState("custom");
  const [customSize, setCustomSize] = useState({ width: 0, height: 0 });
  const [swap, setSwap] = useState(false);
  const [corners, setCorners] = useState([]);
  const [lines, setLines] = useState([]);
  const [pending, setPending] = useState(null);
  const [drag, setDrag] = useState(null);
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);
  const scroller = useRef(null);
  const svg = useRef(null);
  const img = useRef(null);
  const loupe = useRef(null);
  const zoomAnchor = useRef(null);
  const justDragged = useRef(false);

  useEffect(() => () => src && URL.revokeObjectURL(src), [src]);

  const loadPhoto = event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setSrc(URL.createObjectURL(file));
    setNatural(null);
    setCorners([]);
    setLines([]);
    setPending(null);
    setZoom(1);
  };

  // Keep the photo pixel scale current for handle sizes and tap precision.
  useLayoutEffect(() => {
    const el = svg.current;
    if (!el || !natural) return undefined;
    const update = () => {
      const width = el.getBoundingClientRect().width;
      if (width) setScale(natural.w / width);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [natural, zoom, open]);

  // At zoom 1 the whole photo fits the stage, whether it is taken upright or across.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || !natural) return undefined;
    const update = () => {
      const maxHeight = parseFloat(getComputedStyle(el).maxHeight) || el.clientHeight;
      setFit(Math.min(el.clientWidth, (maxHeight * natural.w) / natural.h));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [natural, open]);

  // Zoom around the middle of what is visible.
  useLayoutEffect(() => {
    const el = scroller.current;
    const anchor = zoomAnchor.current;
    if (!el || !anchor) return;
    el.scrollLeft = anchor.x * el.scrollWidth - el.clientWidth / 2;
    el.scrollTop = anchor.y * el.scrollHeight - el.clientHeight / 2;
    zoomAnchor.current = null;
  }, [zoom]);

  const zoomTo = next => {
    const el = scroller.current;
    const value = Math.min(MAX_ZOOM, Math.max(1, next));
    if (el) {
      zoomAnchor.current = {
        x: (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth,
        y: (el.scrollTop + el.clientHeight / 2) / el.scrollHeight
      };
    }
    setZoom(value);
  };

  const reference =
    refKind === "custom"
      ? customSize.width && customSize.height
        ? customSize
        : null
      : REFERENCES[refKind];
  const ordered = corners.length === 4 ? orderCorners(corners) : null;
  const convex = ordered ? isConvex(ordered) : false;
  const targets =
    ordered && convex && reference ? referenceTargets(ordered, reference, swap) : null;

  const results = useMemo(
    () =>
      targets
        ? lines.map(line => measureLine(ordered, targets, line.a, line.b, drag ? 60 : 300))
        : [],
    [targets && targets.flat().join(), ordered && JSON.stringify(ordered), lines, drag]
  );

  const toPhoto = event => {
    const rect = svg.current.getBoundingClientRect();
    const k = natural.w / rect.width;
    return {
      x: Math.min(natural.w, Math.max(0, (event.clientX - rect.left) * k)),
      y: Math.min(natural.h, Math.max(0, (event.clientY - rect.top) * k)),
      s: Math.max(TAP_SIGMA * k, 0.5)
    };
  };

  const handleTap = event => {
    if (justDragged.current) {
      justDragged.current = false;
      return;
    }
    const point = toPhoto(event);
    if (corners.length < 4) {
      setCorners(prev => [...prev, point]);
      return;
    }
    if (!pending) setPending(point);
    else {
      setLines(prev => [...prev, { a: pending, b: point }]);
      setPending(null);
    }
  };

  const drawLoupe = (event, point) => {
    const canvas = loupe.current;
    if (!canvas || !img.current) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = LOUPE_SIZE * ratio;
    canvas.height = LOUPE_SIZE * ratio;
    canvas.style.left = `${event.clientX - LOUPE_SIZE / 2}px`;
    canvas.style.top = `${Math.max(8, event.clientY - LOUPE_SIZE - 48)}px`;
    const ctx = canvas.getContext("2d");
    const half = (LOUPE_SIZE / 2) * (scale / LOUPE_ZOOM);
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      img.current,
      point.x - half,
      point.y - half,
      half * 2,
      half * 2,
      0,
      0,
      canvas.width,
      canvas.height
    );
    const mid = canvas.width / 2;
    ctx.strokeStyle = "#e11d48";
    ctx.lineWidth = ratio;
    ctx.beginPath();
    ctx.moveTo(mid, 0);
    ctx.lineTo(mid, canvas.height);
    ctx.moveTo(0, mid);
    ctx.lineTo(canvas.width, mid);
    ctx.stroke();
  };

  const startDrag = (event, handle) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDrag(handle);
    drawLoupe(event, toPhoto(event));
  };

  const moveDrag = event => {
    if (!drag) return;
    const point = toPhoto(event);
    if (drag.kind === "corner") {
      setCorners(prev => prev.map((p, i) => (i === drag.index ? point : p)));
    } else if (drag.kind === "pending") {
      setPending(point);
    } else {
      setLines(prev =>
        prev.map((line, i) => (i === drag.index ? { ...line, [drag.kind]: point } : line))
      );
    }
    drawLoupe(event, point);
  };

  const endDrag = () => {
    if (!drag) return;
    justDragged.current = true;
    setDrag(null);
    // A click follows a drag on most devices; forget the flag if none comes.
    setTimeout(() => {
      justDragged.current = false;
    }, 350);
  };

  const handle = (point, key, dragHandle, className) => (
    <g key={key} className={className}>
      <circle cx={point.x} cy={point.y} r={HANDLE * scale} className="pm-handle" />
      <circle cx={point.x} cy={point.y} r={1.5 * scale} className="pm-handle-dot" />
      <circle
        cx={point.x}
        cy={point.y}
        r={HIT * scale}
        className="pm-hit"
        onPointerDown={event => startDrag(event, dragHandle)}
      />
    </g>
  );

  const stage = !src ? "photo" : corners.length < 4 ? "reference" : "lines";
  const applyResult = index => {
    target.apply(Math.round(results[index].length));
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="bottom"
      height="100%"
      rootClassName="photo-measure"
      title={
        <div className="pm-title">
          <span>{t("Measure from a photo")}</span>
          {target?.label && <Typography.Text type="secondary">{target.label}</Typography.Text>}
        </div>
      }
      extra={
        src && (
          <Space size={4}>
            <Button
              icon={<ZoomOutOutlined />}
              aria-label={t("Zoom out")}
              disabled={zoom <= 1}
              onClick={() => zoomTo(zoom / 1.6)}
            />
            <Button
              icon={<ZoomInOutlined />}
              aria-label={t("Zoom in")}
              disabled={zoom >= MAX_ZOOM}
              onClick={() => zoomTo(zoom * 1.6)}
            />
          </Space>
        )
      }
    >
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={loadPhoto}
      />
      <input ref={galleryInput} type="file" accept="image/*" hidden onChange={loadPhoto} />

      {stage === "photo" ? (
        <div className="pm-start">
          <Typography.Paragraph>
            {t(
              "Put or find a rectangle of known size on the surface you want to measure: a door, a tile, a stretch of wall you measured once with the tape, or an A4 sheet. Then take a photo that shows the reference and everything you want to measure."
            )}
          </Typography.Paragraph>
          <Space direction="vertical" className="pm-start-actions">
            <Button
              type="primary"
              size="large"
              block
              icon={<CameraOutlined />}
              onClick={() => cameraInput.current.click()}
            >
              {t("Take photo")}
            </Button>
            <Button
              size="large"
              block
              icon={<PictureOutlined />}
              onClick={() => galleryInput.current.click()}
            >
              {t("Choose photo")}
            </Button>
          </Space>
          <Collapse
            ghost
            className="pm-tips"
            items={[
              {
                key: "tips",
                label: t("How to get accurate results"),
                children: (
                  <ul>
                    <li>
                      {t("The bigger the reference, the more accurate: a door beats an A4 sheet.")}
                    </li>
                    <li>
                      {t(
                        "Reference and measured points must lie on the same flat surface (the same wall or the floor)."
                      )}
                    </li>
                    <li>{t("Use the normal camera (1×), not the wide-angle lens (0.5×).")}</li>
                    <li>{t("Zoom in and drag the points exactly onto the corners.")}</li>
                    <li>
                      {t(
                        "Every result shows its uncertainty. For final dimensions, check with the tape or laser."
                      )}
                    </li>
                  </ul>
                )
              }
            ]}
          />
        </div>
      ) : (
        <>
          <Row gutter={[8, 8]} className="pm-reference">
            <Col xs={24} md={10}>
              <Select
                value={refKind}
                onChange={setRefKind}
                className="pm-full"
                options={REFERENCE_OPTIONS.map(option => ({ ...option, label: t(option.label) }))}
              />
            </Col>
            {refKind === "custom" && (
              <>
                <Col xs={12} md={5}>
                  <LengthInput
                    value={customSize.width}
                    placeholder={t("Width")}
                    ariaLabel={t("Width")}
                    onChange={width => setCustomSize(prev => ({ ...prev, width }))}
                  />
                </Col>
                <Col xs={12} md={5}>
                  <LengthInput
                    value={customSize.height}
                    placeholder={t("Height")}
                    ariaLabel={t("Height")}
                    onChange={height => setCustomSize(prev => ({ ...prev, height }))}
                  />
                </Col>
              </>
            )}
            <Col xs={24} md={4} className="pm-swap">
              <Switch size="small" checked={swap} onChange={setSwap} />{" "}
              <Typography.Text type="secondary">{t("Turned")}</Typography.Text>
            </Col>
          </Row>

          <Typography.Paragraph className="pm-step" strong>
            {stage === "reference"
              ? t("Tap the 4 corners of the reference ({{count}} of 4).", {
                  count: corners.length
                })
              : pending
                ? t("Tap the end point.")
                : t("Tap two points to measure the distance between them.")}
          </Typography.Paragraph>

          <div className="pm-stage" ref={scroller}>
            <div
              className="pm-canvas"
              style={{ width: fit ? `${Math.round(fit * zoom)}px` : `${zoom * 100}%` }}
            >
              <img
                ref={img}
                src={src}
                alt=""
                draggable={false}
                onLoad={event =>
                  setNatural({ w: event.target.naturalWidth, h: event.target.naturalHeight })
                }
              />
              {natural && (
                <svg
                  ref={svg}
                  viewBox={`0 0 ${natural.w} ${natural.h}`}
                  preserveAspectRatio="none"
                  onClick={handleTap}
                  onPointerMove={moveDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                >
                  {ordered && (
                    <polygon
                      className={convex ? "pm-ref" : "pm-ref pm-ref--bad"}
                      points={ordered.map(p => `${p.x},${p.y}`).join(" ")}
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
                  {lines.map((line, i) => {
                    const result = results[i];
                    return (
                      <g key={`line${i}`}>
                        <line
                          className="pm-line"
                          x1={line.a.x}
                          y1={line.a.y}
                          x2={line.b.x}
                          y2={line.b.y}
                          vectorEffect="non-scaling-stroke"
                        />
                        {result && (
                          <text
                            className="pm-label"
                            x={(line.a.x + line.b.x) / 2}
                            y={(line.a.y + line.b.y) / 2 - 10 * scale}
                            fontSize={14 * scale}
                            strokeWidth={3 * scale}
                            textAnchor="middle"
                          >
                            {i + 1}: {formatLength(Math.round(result.length), lng)}
                          </text>
                        )}
                      </g>
                    );
                  })}
                  {corners.map((point, i) =>
                    handle(point, `c${i}`, { kind: "corner", index: i }, "pm-corner")
                  )}
                  {lines.map((line, i) => [
                    handle(line.a, `a${i}`, { kind: "a", index: i }, "pm-point"),
                    handle(line.b, `b${i}`, { kind: "b", index: i }, "pm-point")
                  ])}
                  {pending && handle(pending, "pending", { kind: "pending" }, "pm-point")}
                </svg>
              )}
            </div>
          </div>
          <canvas ref={loupe} className={drag ? "pm-loupe pm-loupe--on" : "pm-loupe"} />

          {ordered && !convex && (
            <Alert
              type="error"
              showIcon
              className="pm-alert"
              message={t(
                "The corners do not form a rectangle. Drag them onto the reference corners."
              )}
            />
          )}
          {stage === "lines" && !reference && (
            <Alert
              type="info"
              showIcon
              className="pm-alert"
              message={t("Enter the width and height of your reference.")}
            />
          )}

          {lines.length > 0 && targets && (
            <ul className="pm-results">
              {lines.map((line, i) => {
                const result = results[i];
                if (!result) return null;
                return (
                  <li key={`r${i}`}>
                    <div className="pm-result-value">
                      <Typography.Text strong>
                        {i + 1}: {formatLength(Math.round(result.length), lng)}
                      </Typography.Text>{" "}
                      <Typography.Text type={result.sigma > 20 ? "danger" : "secondary"}>
                        {formatUncertainty(result.sigma, lng)}
                      </Typography.Text>
                      {result.far && (
                        <div className="pm-small">
                          {t("Far from the reference: less accurate. Use a larger reference.")}
                        </div>
                      )}
                      {!result.far && result.sigma > 10 && zoom < 2 && (
                        <div className="pm-small">
                          {t("Zoom in and drag the points exactly onto the corners.")}
                        </div>
                      )}
                    </div>
                    <Space size={4}>
                      {target && (
                        <Button
                          type="primary"
                          icon={<CheckOutlined />}
                          onClick={() => applyResult(i)}
                        >
                          {t("Use")}
                        </Button>
                      )}
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        aria-label={t("Delete")}
                        onClick={() => setLines(prev => prev.filter((_, index) => index !== i))}
                      />
                    </Space>
                  </li>
                );
              })}
            </ul>
          )}

          <Space wrap className="pm-actions">
            <Button icon={<CameraOutlined />} onClick={() => cameraInput.current.click()}>
              {t("New photo")}
            </Button>
            {corners.length > 0 && (
              <Button
                icon={<UndoOutlined />}
                onClick={() => {
                  setCorners([]);
                  setPending(null);
                }}
              >
                {t("Set reference again")}
              </Button>
            )}
          </Space>
        </>
      )}
    </Drawer>
  );
};

export default PhotoMeasure;
